import { createSelector } from 'reselect';
import { groupClients, searchClient, getDateFormatYYYYMMDD } from 'utils';
import _get from 'lodash/get';
import { isBefore } from 'date-fns';

const clientsSelector = (clientsModule) => clientsModule.clientsList.data;
const filtersSelector = (clientsModule) => clientsModule.clientsList.filter;
const searchSelector = (clientsModule) => clientsModule.clientsList.search;
const ordersSelector = (state) => state.clientsModule.orderHistory.clientOrders;
const clientsRecentSelector = (clientsModule) => clientsModule.clientsRecentList.data;
const groupsDetailsSelector = (clientsModule) => clientsModule.groups.groupDetails;
const groupsSelector = (clientsModule) => clientsModule.groups.data;
const groupsSortSelector = (clientsModule, sortBy) => sortBy || clientsModule.groups.sortBy;

const userSelector = (state) => state.user.data;
const notesLoadingSelector = (state) => state.clientsModule.clientDetails.notes.loading;
const notesDataSelector = (state) => state.clientsModule.clientDetails.notes.data;
const noteDetailsSelector = (state) => state.clientsModule.clientDetails.notes.noteDetails.data;
const noteDetailsLoadingSelector = (state) => state.clientsModule.clientDetails.notes.noteDetails.loading;
const notesFilterSelector = (state) => state.clientsModule.clientDetails.notes.filter;
const customerRTCDataSelector = (state) => state.clientsModule.reasonToContact.data;

const loyaltyDataSelector = (state) => state.clientsModule.loyalty.data;

const filterClientsSelector = createSelector(clientsSelector, filtersSelector, (clients, filters) => {
  return filters ? clients.filter((client) => client.conciergeItems) : clients;
});

export const getClientNotesLoading = createSelector(notesLoadingSelector, (loading) => loading);

export const getClientNoteDetailsLoading = createSelector(noteDetailsLoadingSelector, (loading) => loading);

export const getClientNoteDetails = createSelector(noteDetailsSelector, (note) => note);

export const getGroupDetails = createSelector(groupsDetailsSelector, (group) => group.data);

export const getClients = createSelector(filterClientsSelector, searchSelector, (filterClients, search) => {
  const clients = search.length ? filterClients.filter((client) => searchClient(client, search)) : filterClients;
  return groupClients(clients);
});

export const getUngroupedClients = createSelector(clientsSelector, (clients) => clients);

export const getSortedGroups = createSelector(groupsSelector, groupsSortSelector, (groups, sortBy) => {
  const [defaultGroup, ...groupsData] = groups;
  let sortedData = groupsData;

  switch (sortBy) {
    case 'alphabet':
      sortedData = groupsData.sort((a, b) => (b.name.toLowerCase() > a.name.toLowerCase() ? -1 : 1));
      break;
    case 'dateDesc':
      sortedData = groupsData.sort((a, b) => new Date(b.creationTime).getTime() - new Date(a.creationTime).getTime());
      break;
    case 'dateAsc':
      sortedData = groupsData.sort((a, b) => new Date(a.creationTime).getTime() - new Date(b.creationTime).getTime());
      break;
    case 'clientsDesc':
      sortedData = groupsData.sort((a, b) => b.clientsCount - a.clientsCount);
      break;
    case 'clientsAsc':
      sortedData = groupsData.sort((a, b) => a.clientsCount - b.clientsCount);
      break;

    default:
      break;
  }

  return defaultGroup ? [defaultGroup, ...sortedData] : sortedData;
});

export const getFilteredNotes = createSelector(
  userSelector,
  notesDataSelector,
  notesFilterSelector,
  (user, notes, filter) => {
    switch (filter) {
      case 'allNotes':
        return notes;
      case 'myNotes':
        return notes.filter((note) => note.author.associatePin === user.employeeNumber);
      case 'othersNotes':
        return notes.filter((note) => note.author.associatePin !== user.employeeNumber);

      default:
        return [];
    }
  },
);

export const getRecentClients = createSelector(clientsRecentSelector, (clients) => clients);

export const getProductsFarfetch = createSelector(
  (products, clientId) => ({ products, clientId }),
  ({ products, clientId }) => (products.length && products[0].clientId === clientId ? products : []),
);

export const getProducts = createSelector(
  (products, clientId) => ({ products, clientId }),
  ({ products, clientId }) => (products.length && products[0].clientId === clientId ? products : []),
);

export const getMaxEarnedInCircleLevel = createSelector(
  (loyalty) => ({ loyalty }),
  ({ loyalty }) => {
    const loyaltyAccounts = _get(loyalty, ['data', 'loyaltyAccounts'], []);
    return loyaltyAccounts.reduce((acc, { loyaltyAccount }) => {
      const earnedLevel = parseInt(loyaltyAccount.earnedLevel, 10);
      return earnedLevel > acc ? earnedLevel : acc;
    }, 0);
  },
);

export const isGiftWrappable = createSelector(
  (products) => ({ products }),
  ({ products }) => products.every((product) => product.isGiftWrappable),
);

export const getIneligibleItemsForWrapping = createSelector(
  (products) => ({ products }),
  ({ products }) =>
    products.reduce((result, product) => (!product.isGiftWrappable ? [...result, product.itemskuId] : result), []),
);

export const getPerishableItemsWithInvalidDeliveryDate = createSelector(
  (products) => ({ products }),
  ({ products }) =>
    products
      .filter(({ isPerishable, arriveByDate }) => {
        if (isPerishable && arriveByDate) {
          const currentDateYYYYMMDD = getDateFormatYYYYMMDD(new Date());
          // filter product is its delivery date is before today.
          return isBefore(new Date(arriveByDate), new Date(currentDateYYYYMMDD));
        }

        return isPerishable && !arriveByDate;
      })
      .map(({ itemskuId }) => itemskuId),
);

export const getLoyaltyAssetsDetails = createSelector(
  [loyaltyDataSelector, (state, { cardTypeLabel }) => cardTypeLabel],
  (loyaltyDataSelector, cardTypeLabel) => {
    const assets = [...(loyaltyDataSelector.loyaltyAssets || []), ...(loyaltyDataSelector.nonPlccAssets || [])];

    if (!assets) {
      return { hasError: true };
    }

    const { filteredAssets, totalAmount, count } = assets.reduce(
      (acc, el) => {
        const { totalAmount, count, filteredAssets } = acc;

        if (el.cardTypeLabel === cardTypeLabel) {
          return {
            totalAmount: totalAmount + +el.currentBal,
            count: count + 1,
            filteredAssets: [...filteredAssets, el],
          };
        }

        return acc;
      },
      { totalAmount: 0, count: 0, filteredAssets: [] },
    );

    return {
      filteredLoyaltyAssets: filteredAssets,
      assetsSummary: { totalAmount, count },
    };
  },
);

export const getOrdersLoading = createSelector(ordersSelector, (orders) => orders.loading);

const onlyNMProfiles = ['nm', 'nmo'];
const onlyBGProfiles = ['bg', 'bgo'];
const allDisplayedProfiles = ['nm', 'nmo', 'bg', 'bgo'];

const filterProfiles = (profileData, type, toggle) => {
  const profileDataInUse = toggle && profileData?.ucaProfiles ? profileData.ucaProfiles : profileData?.onlineProfiles;

  return (
    profileDataInUse
      ?.filter((profile) => {
        const selectedBrandType = type && (type === 'nm' ? onlyNMProfiles : onlyBGProfiles);
        const profileType = selectedBrandType || allDisplayedProfiles;

        return (
          profileType.includes(profile?.profileType?.toLowerCase()) ||
          profileType.includes(profile?.sourceName?.toLowerCase())
        );
      })
      .map((profile) => {
        const { sourceName } = profile;
        const casedSourceName = sourceName.toUpperCase();

        return {
          ...profile,
          profileType: casedSourceName === 'NM' || casedSourceName === 'BG' ? `${casedSourceName}O` : casedSourceName,
        };
      }) || []
  );
};

export const filterAllDisplayedProfiles = createSelector(
  (props) => props,
  (props) => {
    const { profileData, ucaInfoToggle } = props || {};

    return {
      ...profileData,
      onlineProfiles: filterProfiles(profileData, '', ucaInfoToggle),
      nmOnlyProfiles: filterProfiles(profileData, 'nm', ucaInfoToggle),
      bgOnlyProfiles: filterProfiles(profileData, 'bg', ucaInfoToggle),
    };
  },
);

export const selectCategoryRTCItems = (categoryName) =>
  createSelector(customerRTCDataSelector, (categories) =>
    categories.filter(({ customerRTCType }) => customerRTCType.toLowerCase() === categoryName),
  );

export const getClientProfilesIds = createSelector(
  (profileData) => profileData,
  ({ profileData }) => ({
    webProfileIds: [...new Set(profileData?.onlineProfiles.map((item) => item.profileId))],
    posIds: profileData?.posIds || [],
    cmosIds: profileData?.cmosIds || [],
  }),
);

export const getJeweleryProductIds = (conciergeProducts) => {
  if (!conciergeProducts || !conciergeProducts.length) return [];
  return conciergeProducts.reduce((result, { isIce, offerItemId, itemskuId }) => {
    const isJeweleryProduct = offerItemId.includes('_P') || offerItemId.includes('_J');
    if (isIce || isJeweleryProduct) {
      return [...result, itemskuId];
    }
    return result;
  }, []);
};
